import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTEREST_OPTIONS = ["Capital Funding", "FMCG", "General Enquiry"];

export default function ContactSection() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "Capital Funding",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.message.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Thank you. We'll be in touch soon.");
      setForm({
        name: "",
        email: "",
        phone: "",
        interest: "Capital Funding",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not submit. Please try again or call us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0A1F44] text-white py-24 md:py-32 cgp-grain overflow-hidden"
    >
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#C8A24A]/5 blur-3xl pointer-events-none" />

      <div className="cgp-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left — Info */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <span className="cgp-overline">Get in Touch</span>
            <h2 className="font-serif font-light text-white mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Let's build
              <br />
              <span className="italic text-white/65">something enduring.</span>
            </h2>
            <p className="mt-7 text-white/70 leading-relaxed max-w-md">
              Whether you're seeking capital, exploring a brand partnership, or
              simply want to understand what Catalyst can do — we'd love to hear
              from you.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-start gap-4" data-testid="contact-info-phones">
                <div className="flex items-center justify-center w-11 h-11 rounded-full bg-[#C8A24A]/10 border border-[#C8A24A]/30 text-[#C8A24A] shrink-0">
                  <Phone size={16} strokeWidth={1.7} />
                </div>
                <div>
                  <div className="text-white/55 text-[11px] uppercase tracking-[0.22em]">
                    Phone
                  </div>
                  <div className="mt-2 flex flex-col gap-1.5">
                    <a
                      href="tel:+919891892778"
                      data-testid="contact-phone-1"
                      className="text-white text-base md:text-lg hover:text-[#C8A24A] transition-colors"
                    >
                      +91 98918 92778
                    </a>
                    <a
                      href="tel:+919810700890"
                      data-testid="contact-phone-2"
                      className="text-white text-base md:text-lg hover:text-[#C8A24A] transition-colors"
                    >
                      +91 98107 00890
                    </a>
                    <a
                      href="tel:+919899940615"
                      data-testid="contact-phone-3"
                      className="text-white text-base md:text-lg hover:text-[#C8A24A] transition-colors"
                    >
                      +91 98999 40615
                    </a>
                  </div>
                </div>
              </div>
              <ContactRow
                icon={Mail}
                label="Email"
                value="sales@catalystglobalpartners.in"
                href="mailto:sales@catalystglobalpartners.in"
                testId="contact-info-email"
              />
              <ContactRow
                icon={MapPin}
                label="Office Hours"
                value="Mon – Sat · 10:00 AM – 7:00 PM IST"
                testId="contact-info-hours"
              />
            </div>
          </motion.div>

          {/* Right — Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="bg-[#051024]/70 border border-white/10 backdrop-blur-sm p-8 md:p-10 rounded-sm"
            >
              <h3 className="font-serif text-2xl md:text-3xl text-white">
                Send an enquiry
              </h3>
              <span className="cgp-gold-rule mt-4" />

              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Full Name" required>
                  <input
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={handleChange("name")}
                    placeholder="Your full name"
                    className="cgp-input"
                    required
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={handleChange("email")}
                    placeholder="you@company.com"
                    className="cgp-input"
                    required
                  />
                </Field>
                <Field label="Phone" required>
                  <input
                    data-testid="contact-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange("phone")}
                    placeholder="+91 98XXXXXXXX"
                    className="cgp-input"
                    required
                  />
                </Field>
                <Field label="I'm interested in" required>
                  <select
                    data-testid="contact-input-interest"
                    value={form.interest}
                    onChange={handleChange("interest")}
                    className="cgp-input appearance-none cursor-pointer"
                  >
                    {INTEREST_OPTIONS.map((opt) => (
                      <option key={opt} value={opt} className="bg-[#051024]">
                        {opt}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Message" required>
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={handleChange("message")}
                    placeholder="Tell us briefly about your business and what you need..."
                    className="cgp-input"
                    required
                  />
                </Field>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-white/55 text-xs">
                  We respond within 1 business day.
                </p>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={submitting}
                  className="cgp-btn-gold rounded-full px-7 py-3.5 inline-flex items-center gap-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Enquiry
                      <Send size={15} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-white/70 text-xs uppercase tracking-[0.2em] mb-2 block">
        {label}
        {required && <span className="text-[#C8A24A] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label, value, href, testId }) {
  const content = (
    <div className="flex items-start gap-4 group">
      <div className="flex items-center justify-center w-11 h-11 rounded-full bg-[#C8A24A]/10 border border-[#C8A24A]/30 text-[#C8A24A] shrink-0 group-hover:bg-[#C8A24A] group-hover:text-[#051024] transition-colors">
        <Icon size={16} strokeWidth={1.7} />
      </div>
      <div>
        <div className="text-white/55 text-[11px] uppercase tracking-[0.22em]">
          {label}
        </div>
        <div className="text-white text-base md:text-lg mt-1 group-hover:text-[#C8A24A] transition-colors">
          {value}
        </div>
      </div>
    </div>
  );
  if (href) {
    return (
      <a href={href} data-testid={testId}>
        {content}
      </a>
    );
  }
  return <div data-testid={testId}>{content}</div>;
}
